import { captureException } from '@sentry/react';
import { Button } from 'base/ui/button';
import { IconThumbsDown, IconThumbsUp } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { LottiePlayer } from 'base/ui/lottie';
import { DividerLine, Panel, PanelHeader } from 'base/ui/panel';
import { Textarea } from 'base/ui/textarea';
import {
  EXTRA_LONG_TOAST_DURATION,
  LONG_TOAST_DURATION,
  ToastQueue,
} from 'base/ui/toast';
import { TextSmallLight } from 'base/ui/typography';
import { LLMProxyService } from 'blocks/llm_proxy_service';
import { computed, makeObservable, observable, reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { createPortal } from 'react-dom';
import { editorModeStore } from '../editor_mode';
import animatedStarsLottie from './animated_stars.lottie.json';
import { AiTask, AiTaskLog } from './task_log';
import styles from './toasts.css';

/**
 * AI toasts operate in an insane 3 step lifecycle:
 *
 *  0. No tasks, no toast
 *  1. Some tasks are in progress -> show the progress toast
 *    (We save those tasks into tasksDisplayed)
 *  2. Tasks are no longer in progress -> show the feedback toast (if they had usageIds)
 *    (We clear tasksDisplayed here)
 *  3. Feedback toast is clicked -> show the feedback popup
 **/
class ToastsStore {
  constructor() {
    makeObservable(this);
  }

  readonly tasksDisplayed = observable.set<AiTask>([], { deep: false });

  get tasksDisplayedUsageIds() {
    return Array.from(this.tasksDisplayed).flatMap((t) => t.llmUsageIds);
  }

  // Step 3 - when the feedback toast is clicked, a popup is displayed instead.
  @observable.ref popupState:
    | {
        usageIds: readonly number[];
        score: 1 | -1;
      }
    | undefined = undefined;
}

export function installAiToasts(
  assistant: Pick<LLMProxyService, 'postFeedback'>,
  taskLog: AiTaskLog,
  toastQueue: ToastQueue,
  sessionType: 'logged-in' | 'anonymous',
) {
  const stopPendingTasks = () => {
    for (const t of taskLog.pendingTasks) {
      t.stop();
    }
  };

  const store = new ToastsStore();

  const disposeErrorToasts = reaction(
    () => taskLog.failedTasks,
    (failedTasks, previous) => {
      for (const task of failedTasks.values()) {
        // Exit early for non-errors and errors that have already been toasted
        if (task.completed?.success !== false || previous.has(task)) {
          continue;
        }

        toastQueue.push({
          theme: 'dark',
          label: task.completed.errorText,
          duration: EXTRA_LONG_TOAST_DURATION,
        });
      }
    },
  );

  const tasksToDisplay = computed(() =>
    taskLog.pendingTasks.filter(
      (t) =>
        t.meta.onlyNotifyIn == null ||
        t.meta.onlyNotifyIn === editorModeStore.get(),
    ),
  );
  const disposeTaskCopier = reaction(
    () => tasksToDisplay.get(),
    (tasks) => tasks.forEach((t) => store.tasksDisplayed.add(t)),
  );
  const disposeProgressToast = toastQueue.addPersistent(() => {
    const titles = [
      ...new Set(
        tasksToDisplay
          .get()
          .map((t) => t.meta.titleWhenRunning)
          .map((title) => (typeof title === 'string' ? title : title())),
      ),
    ];
    if (titles.length === 0) return;
    const title = titles.length === 1 ? titles[0] : `Generating many things...`;
    return {
      customLeft: (
        <div className={styles.icon}>
          <LottiePlayer animationData={animatedStarsLottie} />
        </div>
      ),
      theme: 'light',
      label: title,
      customRight: <StoppableSpinner onStop={stopPendingTasks} />,
    };
  });

  let removeFeedbackToast: (() => void) | undefined;
  const disposeShowFeedbackToast = reaction(
    () => tasksToDisplay.get().length > 0,
    (isInProgress) => {
      removeFeedbackToast?.();
      if (isInProgress) return;
      if (sessionType === 'anonymous') return;
      const usageIds = store.tasksDisplayedUsageIds;
      if (!usageIds.length) return;

      removeFeedbackToast = toastQueue.push({
        label: 'AI feedback',
        customRight: (
          <Inline spacing={2}>
            <div />
            <Button
              variant="primary"
              size="small"
              icon={IconThumbsUp}
              onClick={() => {
                store.popupState = { usageIds, score: 1 };
                removeFeedbackToast?.();
              }}
              tooltip="It worked well"
            />
            <Button
              variant="primary"
              size="small"
              icon={IconThumbsDown}
              onClick={() => {
                store.popupState = { usageIds, score: -1 };
                removeFeedbackToast?.();
              }}
              tooltip="It didn't work well"
            />
          </Inline>
        ),
        duration: LONG_TOAST_DURATION,
      });
    },
  );

  return {
    dispose: () => {
      disposeErrorToasts();
      disposeTaskCopier();
      disposeProgressToast();
      disposeShowFeedbackToast();
    },
    feedbackLayer: (
      <FeedbackPopup
        assistant={assistant}
        store={store}
        toastQueue={toastQueue}
      />
    ),
  };
}

export function StoppableSpinner({ onStop }: { onStop: () => void }) {
  return (
    <div className={styles.spinner} onClick={onStop}>
      <div className={styles.spinnerFull} />
      <div className={styles.spinnerPart} />
      <div className={styles.spinnerStop} />
    </div>
  );
}

const FeedbackPopup = observer(function _FeedbackPopup({
  store,
  assistant,
  toastQueue,
}: {
  store: ToastsStore;
  assistant: Pick<LLMProxyService, 'postFeedback'>;
  toastQueue: ToastQueue;
}) {
  const [submitting, setSubmitting] = React.useState(false);
  const [comment, setComment] = React.useState('');

  const onClose = () => {
    store.popupState = undefined;
  };

  if (!store.popupState) return null;

  const { usageIds, score } = store.popupState;
  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await assistant.postFeedback(score, comment, usageIds);
      toastQueue.push({ label: 'Thanks for submitting feedback!' });
    } catch (e) {
      captureException(e);
    } finally {
      onClose();
    }
  };
  return createPortal(
    <div className={styles.popup}>
      <Panel>
        <Rows spacing={16}>
          <PanelHeader
            title="AI Feedback"
            navigation={{
              close: () => {
                submit();
                onClose();
              },
            }}
          />
          <DividerLine expandToEdges={true} />
          <TextSmallLight>
            <Textarea
              ref={focusOnMount}
              placeholder="How could the output be improved? (optional)"
              value={comment}
              onChange={setComment}
              minHeight={80}
            />
          </TextSmallLight>
          <Button onClick={submit} label="Submit" loading={submitting} />
        </Rows>
      </Panel>
    </div>,
    document.body,
  );
});

function focusOnMount(el: HTMLTextAreaElement | null) {
  el?.focus();
}

export function showAllPagesCelebrationToast(toastQueue: ToastQueue) {
  toastQueue.push({
    confetti: true,
    theme: 'light',
    label: 'Boom! Sitemap Complete',
  });
}
